import React, { useEffect, useRef } from 'react';
import Paper from '@material-ui/core/Paper';
import { Box, Divider, makeStyles } from '@material-ui/core';
import PrmTitleRow from '../../common/PrmTitleRow';
import useCommonStyles from '../../common/styles';
import SkillProgressTable from './SkillProgressTable';
import SkillProgressChart from './SkillProgressChart';
import { ReportEntityType } from 'components/ReportsModule/types';
import { capitalize } from 'lodash';
import { PrmStudentCurriculumLessonReportResult } from 'types/schema-generated-types';
import StudentLargeBarChart from './StudentLargeBarChart';
import { buildChartValues } from './util';
import Maybe from 'components/@common/Maybe';
import StyledSkeleton from 'components/@common/Skeleton';

type Props = {
  entityType: ReportEntityType;
  studentLessonReport: PrmStudentCurriculumLessonReportResult;
  setList?: any[];
  selected: any;
  setSelected: any;
  loading: boolean;
};

const StudentSkillsProgress: React.FC<Props> = ({
  entityType,
  setList,
  selected,
  setSelected,
  studentLessonReport
}) => {
  const containerRef = useRef<HTMLElement>();
  const commonClasses = useCommonStyles();
  const classes = useStyles();
  const toPx = (val: any) => (val ? `${val}px` : undefined);

  useEffect(() => {
    if (!setList?.[0]?.id) return;
    setSelected(setList?.[0]?.id);
    // eslint-disable-next-line
  }, [setList?.[0]?.id]);

  const {
    completedAndMet,
    allCompleted,
    lessonsWithSupports,
    lessonsWithoutSupports,
    unmetLessons
  } = buildChartValues(studentLessonReport);

  return (
    <Paper ref={containerRef} className={commonClasses.container}>
      {studentLessonReport ? (
        <>
          <Box display="flex" justifyContent="space-between" margin="24px 0">
            <PrmTitleRow
              title={`${capitalize(entityType)} Skills Progress`}
              setList={setList!}
              handleClick={setSelected}
              selected={selected!}
            />
          </Box>
          <Box className={classes.boxContainer}>
            <Box>
              <SkillProgressChart
                total={studentLessonReport?.curriculumDetail?.totalLessonCount!}
                completed={allCompleted}
                assigned={studentLessonReport?.curriculumDetail?.lessonAssignedCount ?? 0}
              />
            </Box>
            <Divider orientation="vertical" style={{ height: '174px' }} />
            <Box display="flex" flex={1} justifyContent="center" flexBasis={350}>
              <Box display="flex" flex={0.7} maxWidth={500}>
                <Maybe shouldRender={allCompleted > 0}>
                  <StudentLargeBarChart
                    total={studentLessonReport.curriculumDetail?.totalLessonCount}
                    completedAndMet={completedAndMet}
                    usedSupports={lessonsWithSupports}
                    didNotUseSupports={lessonsWithoutSupports}
                    unmet={unmetLessons}
                  />
                </Maybe>
              </Box>
            </Box>
          </Box>
          <Box>{<SkillProgressTable studentLessonReport={studentLessonReport} />}</Box>
        </>
      ) : (
        <>
          <PrmTitleRow
            title={`${capitalize(entityType)} Skills Progress`}
            setList={setList!}
            handleClick={setSelected}
            selected={selected!}
          />
          <Box display="flex" flex={1} justifyContent="center">
            <Box display="flex" flex={1}>
              <StyledSkeleton width={'100%'} height={toPx(380)} />
            </Box>
          </Box>
        </>
      )}
    </Paper>
  );
};

const useStyles = makeStyles(() => ({
  boxContainer: {
    display: 'flex',
    flex: 1,
    justifyContent: 'space-evenly',
    alignItems: 'center',
    flexFlow: 'wrap'
  }
}));

export default StudentSkillsProgress;
